'use client';

import { FC, useCallback, useRef } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import Image from 'next/image';
import velxFlowSvg from '@/assets/images/velx-flow.svg';

/**
 * Props for `ThreePhaseFlow`.
 */
export type ThreePhaseFlowProps =
  SliceComponentProps<Content.ThreePhaseFlowSlice>;

const MAX_OFFSET = 10; // max px the cards can shift in any direction

/**
 * Component for "ThreePhaseFlow" Slices.
 */
const ThreePhaseFlow: FC<ThreePhaseFlowProps> = ({ slice }) => {
  const { phases } = slice.primary;
  const kicker = (slice.primary as unknown as { kicker?: string }).kicker;
  const title = (
    slice.primary as unknown as {
      title?: import('@prismicio/client').RichTextField;
    }
  ).title;
  const sectionRef = useRef<HTMLElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  const handleSectionMouseMove = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const section = sectionRef.current;
      if (!section) {
        return;
      }
      const rect = section.getBoundingClientRect();
      // -1 to 1 based on mouse position relative to section center
      const x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      const y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
      const offsetX = x * MAX_OFFSET;
      const offsetY = y * MAX_OFFSET;
      cardRefs.current.forEach((card) => {
        if (card) {
          card.style.transform = `translate(${offsetX}px, ${offsetY}px)`;
        }
      });
    },
    [],
  );

  const handleSectionMouseLeave = useCallback(() => {
    cardRefs.current.forEach((card) => {
      if (card) {
        card.style.transform = '';
      }
    });
  }, []);

  return (
    <section
      ref={sectionRef}
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="flow-section"
      onMouseMove={handleSectionMouseMove}
      onMouseLeave={handleSectionMouseLeave}
    >
      {(kicker || isFilled.richText(title)) && (
        <div className="flow-header">
          {kicker && <span className="flow-kicker">{kicker}</span>}
          {isFilled.richText(title) && (
            <PrismicRichText
              field={title}
              components={{
                heading2: ({ children }) => (
                  <h2 className="flow-title">{children}</h2>
                ),
                strong: ({ children }) => (
                  <strong className="flow-title-accent">{children}</strong>
                ),
              }}
            />
          )}
        </div>
      )}
      <div className="flow-stage">
        <div className="central-logo">
          <Image
            src={velxFlowSvg}
            alt=""
            className="w-full h-full object-contain"
          />
        </div>
        {phases &&
          phases.length > 0 &&
          phases.map((phase, index) => (
            <div
              key={index}
              ref={(el) => {
                cardRefs.current[index] = el;
              }}
              className={'flow-card flow-card-' + index}
            >
              {/* Icon */}
              <div className="flex flex-col items-center">
                {phase.icon && (
                  <div className="w-16 h-16 md:w-20 md:h-20">
                    <PrismicNextImage
                      field={phase.icon}
                      className="w-full h-full object-contain"
                      fallbackAlt=""
                    />
                  </div>
                )}

                {phase.phase_title && (
                  <p className="phase-title">{phase.phase_title}</p>
                )}
              </div>

              {phase.phase_description && (
                <div className="phase-desc">
                  <PrismicRichText
                    field={phase.phase_description}
                    components={{
                      paragraph: ({ children }) => <p>{children}</p>,
                    }}
                  />
                </div>
              )}
              {phase.summary && (
                <div className="phase_summary">
                  <PrismicRichText
                    field={phase.summary}
                    components={{
                      paragraph: ({ children }) => <p>{children}</p>,
                    }}
                  />
                </div>
              )}
            </div>
          ))}
      </div>
      <style jsx>{`
        .flow-header {
          position: relative;
          z-index: 2;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 24px;
          padding: 80px 16px 0;
          text-align: center;
        }
        .flow-kicker {
          border: 1px solid #00f557;
          border-radius: 9999px;
          padding: 6px 16px;
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #00f557;
        }
        .flow-title {
          max-width: 720px;
          margin: 0;
          font-size: 36px;
          font-weight: 700;
          line-height: 1.2;
          color: #ffffff;
        }
        .flow-title-accent {
          color: #00f557;
          font-weight: 700;
        }
        @media (min-width: 768px) {
          .flow-title {
            font-size: 44px;
          }
        }
        .flow-stage {
          position: relative;
          height: 1226px;
        }
        .central-logo {
          position: absolute;
          width: 285px;
          height: 285px;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
        .flow-section {
          background: linear-gradient(180deg, #0e1110 0%, #0f423a 100%);
          position: relative;
          overflow: hidden;
        }
        .flow-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 36px 24px 36px 24px;
          background: linear-gradient(
            112.83deg,
            rgba(255, 255, 255, 0.3) 0%,
            rgba(255, 255, 255, 0) 70%
          );
          backdrop-filter: blur(21px);

          border-radius: 20px;
          flex: none;
          order: 0;
          align-self: stretch;
          flex-grow: 0;
          gap: 8px;
          position: absolute;
          transition:
            transform 0.3s ease,
            box-shadow 0.3s ease,
            background 0.3s ease;
          cursor: pointer;
        }
        .flow-card:hover {
          box-shadow:
            0 8px 32px rgba(0, 245, 87, 0.15),
            0 0 0 1px rgba(0, 245, 87, 0.1);
          background: linear-gradient(
            112.83deg,
            rgba(255, 255, 255, 0.4) 0%,
            rgba(255, 255, 255, 0.05) 70%
          );
        }
        .flow-card-0 {
          top: 10%;
          left: 10%;
          max-width: 414px;
        }
        .flow-card-1 {
          top: 33%;
          right: 6%;
          max-width: 377px;
        }
        .flow-card-2 {
          top: 62%;
          left: 17%;
          max-width: 350px;
        }
        .phase-title {
          font-style: normal;
          font-weight: 600;
          font-size: 20px;
          line-height: 150%;
          text-align: center;
          color: #00f557;
        }
        .phase-desc {
          font-style: normal;
          font-weight: 300;
          font-size: 18px;
          line-height: 150%;
          text-align: center;
          color: #ffffff;
        }
        .phase_summary {
          font-style: normal;
          font-weight: 600;
          font-size: 20px;
          line-height: 150%;
          text-align: center;
          color: #ffffff;
        }
        @media (max-width: 768px) {
          .flow-header {
            padding-top: 48px;
          }
          .flow-stage {
            height: auto;
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 48px 16px;
            gap: 24px;
          }
          .central-logo {
            position: relative;
            top: auto;
            left: auto;
            transform: none;
          }
          .flow-card {
            position: relative;
            top: auto;
            left: auto;
            right: auto;
            width: 344px;
            max-width: 344px;
            align-self: center;
          }
          .flow-card-0,
          .flow-card-1,
          .flow-card-2 {
            top: auto;
            left: auto;
            right: auto;
            max-width: 344px;
          }
        }
      `}</style>
    </section>
  );
};

export default ThreePhaseFlow;
